import { AuthBaseClient } from '../base-client';
import type {
  TeamSummaryResponse,
  TeamWithMembersResponse,
  TeamMemberResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  AddTeamMemberRequest,
  UpdateTeamMemberRequest,
} from './types';

/**
 * Client for team API endpoints.
 */
export class TeamClient extends AuthBaseClient {
  /**
   * Get all teams for the organization.
   */
  async getAll(): Promise<TeamSummaryResponse[]> {
    return this.get<TeamSummaryResponse[]>('/api/teams');
  }

  /**
   * Get teams by channel.
   */
  async getByChannel(channel: string): Promise<TeamSummaryResponse[]> {
    return this.get<TeamSummaryResponse[]>(`/api/teams/channel/${channel}`);
  }

  /**
   * Get inbound default team for a channel.
   */
  async getInboundDefault(channel: string): Promise<TeamWithMembersResponse | null> {
    return this.get<TeamWithMembersResponse | null>(`/api/teams/inbound-default/${channel}`);
  }

  /**
   * Get team by ID with members.
   */
  async getById(id: string): Promise<TeamWithMembersResponse> {
    return this.get<TeamWithMembersResponse>(`/api/teams/${id}`);
  }

  /**
   * Create a new team.
   */
  async create(data: CreateTeamRequest): Promise<TeamWithMembersResponse> {
    return this.post<TeamWithMembersResponse>('/api/teams', data);
  }

  /**
   * Update a team.
   */
  async update(id: string, data: UpdateTeamRequest): Promise<TeamWithMembersResponse> {
    return this.patch<TeamWithMembersResponse>(`/api/teams/${id}`, data);
  }

  /**
   * Delete a team (soft delete).
   */
  async deleteTeam(id: string): Promise<void> {
    return this.delete<void>(`/api/teams/${id}`);
  }

  /**
   * Set team as inbound default for its channel.
   */
  async setAsDefault(id: string): Promise<TeamWithMembersResponse> {
    return this.post<TeamWithMembersResponse>(`/api/teams/${id}/set-default`, {});
  }

  // ===========================================================================
  // Team Members
  // ===========================================================================

  /**
   * Get members for a team.
   */
  async getMembers(teamId: string): Promise<TeamMemberResponse[]> {
    return this.get<TeamMemberResponse[]>(`/api/teams/${teamId}/members`);
  }

  /**
   * Add a member to a team.
   */
  async addMember(teamId: string, data: AddTeamMemberRequest): Promise<TeamMemberResponse> {
    return this.post<TeamMemberResponse>(`/api/teams/${teamId}/members`, data);
  }

  /**
   * Update a team member.
   */
  async updateMember(
    teamId: string,
    memberId: string,
    data: UpdateTeamMemberRequest
  ): Promise<TeamMemberResponse> {
    return this.patch<TeamMemberResponse>(`/api/teams/${teamId}/members/${memberId}`, data);
  }

  /**
   * Remove a member from a team.
   */
  async removeMember(teamId: string, memberId: string): Promise<void> {
    return this.delete<void>(`/api/teams/${teamId}/members/${memberId}`);
  }
}
